import React from 'react';
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  FormControl,
  FormLabel,
  Box,
  Typography,
  Stack,
} from '@mui/material';

/**
 * Design System Color Constants
 */
const COLORS = {
  ACCENT: '#005e5d',
  ACCENT_DARK: '#004d4c',
  ACCENT_LIGHT: '#005e5d14',
  SURFACE_SECONDARY: '#F2F3F5',
  BORDER: '#E6E7EB',
  TEXT_PRIMARY: '#333333',
  TEXT_SECONDARY: '#666666',
  TEXT_TERTIARY: '#999999',
  DESTRUCTIVE: '#D32F2F',
  SUCCESS: '#49CC90',
};

export interface CheckboxOption {
  label: string;
  value: string | number;
  description?: string;
  disabled?: boolean;
}

export interface BaseCheckboxProps {
  /**
   * Single checkbox label (if using single mode)
   */
  label?: string;
  /**
   * Is checkbox checked
   */
  checked?: boolean;
  /**
   * Change handler for single checkbox
   */
  onChange?: (checked: boolean) => void;
  /**
   * Helper text
   */
  helperText?: string;
  /**
   * Is field required
   */
  required?: boolean;
  /**
   * Show error state
   */
  error?: boolean;
  /**
   * Disable checkbox
   */
  disabled?: boolean;
  /**
   * Description text
   */
  description?: string;
  /**
   * Indeterminate state (for parent checkbox in tree)
   */
  indeterminate?: boolean;
}

export interface BaseCheckboxGroupProps {
  /**
   * Array of checkbox options for group
   */
  options: CheckboxOption[];
  /**
   * Selected values
   */
  values: (string | number)[];
  /**
   * Change handler
   */
  onChange?: (values: (string | number)[]) => void;
  /**
   * Label for the group
   */
  label?: string;
  /**
   * Helper text
   */
  helperText?: string;
  /**
   * Layout direction
   */
  direction?: 'row' | 'column';
  /**
   * Is field required
   */
  required?: boolean;
  /**
   * Show error state
   */
  error?: boolean;
  /**
   * Disable all options
   */
  disabled?: boolean;
  /**
   * Show descriptions
   */
  showDescriptions?: boolean;
  /**
   * Spacing between items
   */
  spacing?: number;
}

/**
 * BaseCheckbox Component
 *
 * Premium checkbox component following the Pulse API design system.
 * Supports both single checkbox and checkbox groups.
 *
 * @example
 * ```tsx
 * // Single checkbox
 * <BaseCheckbox
 *   label="I agree to terms"
 *   checked={agreed}
 *   onChange={setAgreed}
 * />
 *
 * // Checkbox with description
 * <BaseCheckbox
 *   label="Enable notifications"
 *   description="Receive updates about your account"
 *   checked={notif}
 *   onChange={setNotif}
 * />
 * 
 * // Checkbox group
 * <BaseCheckboxGroup
 *   label="Select features"
 *   options={[
 *     { label: 'Feature 1', value: 'f1' },
 *     { label: 'Feature 2', value: 'f2' },
 *   ]}
 *   values={selected}
 *   onChange={setSelected}
 * />
 * ```
 */
export const BaseCheckbox = React.forwardRef<HTMLDivElement, BaseCheckboxProps>(
  (
    {
      label,
      checked = false,
      onChange,
      helperText,
      required = false,
      error = false,
      disabled = false,
      description,
      indeterminate = false,
    },
    ref
  ) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event.target.checked);
    };

    return (
      <Box ref={ref} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              indeterminate={indeterminate}
              onChange={handleChange}
              disabled={disabled}
              required={required}
              sx={{
                color: COLORS.BORDER,
                '&.Mui-checked': {
                  color: COLORS.ACCENT,
                },
                '&.Mui-indeterminate': {
                  color: COLORS.ACCENT,
                },
                '&.Mui-disabled': {
                  color: COLORS.SURFACE_SECONDARY,
                },
                transition: 'color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          }
          label={
            label && (
              <Box sx={{ ml: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: disabled ? COLORS.TEXT_TERTIARY : COLORS.TEXT_PRIMARY,
                    fontFamily: '"Manrope", sans-serif',
                  }}
                >
                  {label}
                  {required && <span style={{ color: COLORS.DESTRUCTIVE }}>*</span>}
                </Typography>
                {description && (
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: COLORS.TEXT_TERTIARY,
                      fontWeight: 400,
                      mt: 0.25,
                      fontFamily: '"Manrope", sans-serif',
                    }}
                  >
                    {description}
                  </Typography>
                )}
              </Box>
            )
          }
          sx={{
            mb: 0,
            '&:hover': {
              backgroundColor: !disabled ? COLORS.SURFACE_SECONDARY : 'transparent',
              borderRadius: '8px',
              transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            },
          }}
        />

        {helperText && (
          <Typography
            sx={{
              fontSize: '11px',
              color: error ? COLORS.DESTRUCTIVE : COLORS.TEXT_TERTIARY,
              fontWeight: 500,
              fontFamily: '"Manrope", sans-serif',
            }}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    );
  }
);

BaseCheckbox.displayName = 'BaseCheckbox';

/**
 * BaseCheckboxGroup Component
 *
 * Multiple checkboxes grouped together with consistent styling.
 */
export const BaseCheckboxGroup: React.FC<BaseCheckboxGroupProps> = ({
  options,
  values,
  onChange,
  label,
  helperText,
  direction = 'column',
  required = false,
  error = false,
  disabled = false,
  showDescriptions = false,
  spacing = 1.5,
}) => {
  const handleChange = (optionValue: string | number) => {
    const newValues = values.includes(optionValue)
      ? values.filter((v) => v !== optionValue)
      : [...values, optionValue];
    onChange?.(newValues);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <FormControl
        disabled={disabled}
        error={error}
        component="fieldset"
        required={required}
      >
        {label && (
          <FormLabel
            sx={{
              color: error ? COLORS.DESTRUCTIVE : COLORS.TEXT_PRIMARY,
              fontWeight: 600,
              fontSize: '13px',
              mb: 1,
              fontFamily: '"Manrope", sans-serif',
            }}
          >
            {label}
            {required && <span style={{ color: COLORS.DESTRUCTIVE }}>*</span>}
          </FormLabel>
        )}

        <Stack
          direction={direction}
          spacing={spacing}
          sx={{
            display: 'flex',
            flexDirection: direction,
            gap: spacing,
          }}
        >
          {options.map((option, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={values.includes(option.value)}
                  onChange={() => handleChange(option.value)}
                  disabled={option.disabled || disabled}
                  sx={{
                    color: COLORS.BORDER,
                    '&.Mui-checked': {
                      color: COLORS.ACCENT,
                    },
                    '&.Mui-disabled': {
                      color: COLORS.SURFACE_SECONDARY,
                    },
                    transition: 'color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              }
              label={
                <Box sx={{ ml: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: option.disabled ? 400 : 500,
                      color: option.disabled ? COLORS.TEXT_TERTIARY : COLORS.TEXT_PRIMARY,
                      fontFamily: '"Manrope", sans-serif',
                    }}
                  >
                    {option.label}
                  </Typography>
                  {showDescriptions && option.description && (
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: COLORS.TEXT_TERTIARY,
                        fontWeight: 400,
                        mt: 0.25,
                        fontFamily: '"Manrope", sans-serif',
                      }}
                    >
                      {option.description}
                    </Typography>
                  )}
                </Box>
              }
              sx={{
                mb: 0,
                '&:hover': {
                  backgroundColor: !disabled ? COLORS.SURFACE_SECONDARY : 'transparent',
                  borderRadius: '8px',
                  transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                },
              }}
            />
          ))}
        </Stack>

        {helperText && (
          <FormHelperText
            sx={{
              color: error ? COLORS.DESTRUCTIVE : COLORS.TEXT_TERTIARY,
              fontSize: '11px',
              fontWeight: 500,
              mt: 1,
              fontFamily: '"Manrope", sans-serif',
            }}
          >
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

BaseCheckboxGroup.displayName = 'BaseCheckboxGroup';

export default BaseCheckbox;
